// SpendFields.tsx — amount + GST + campaign split inputs (ADMIN ONLY).
// Used in the push step and in SpendEditModal. Pure UI: the parent owns the
// state and calls checkSpendForm() to get the request body (or an error).

import React from 'react';
import { Wand2 } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { SpendInput, SpendSplit, SyncSpend, campaignLabel, fmtINR, inclExcl, parseRupees, splitByLeads } from './spendTypes';

const MAX_PAISE = 100_000_000 * 100; // ₹10 crore — same cap as the API

export interface SpendFormState {
  amountText: string;
  gst: boolean; // true = the amount already includes 18% GST
  splitMode: 'auto' | 'manual';
  splitText: Record<string, string>; // campaign_key → rupees typed
}

export const emptySpendForm = (): SpendFormState => ({ amountText: '', gst: true, splitMode: 'auto', splitText: {} });

const rupeesText = (paise: number) => String(paise / 100);

export const formFromSpend = (s: SyncSpend): SpendFormState => ({
  amountText: rupeesText(s.amount_paise),
  gst: s.gst_included,
  splitMode: s.split_mode === 'manual' ? 'manual' : 'auto',
  splitText: Object.fromEntries((s.splits ?? []).map((x) => [x.campaign_key, rupeesText(x.amount_paise)])),
});

export interface SpendCheck {
  /** Total in paise, null when nothing typed. */
  paise: number | null;
  error: string | null;
  /** Request body, or null when the amount is empty / invalid. */
  input: SpendInput | null;
  /** Sum of the per-campaign amounts in manual mode. */
  splitSum: number;
  /** Which part the error is about. */
  field?: 'amount' | 'split';
}

export const checkSpendForm = (f: SpendFormState, campaigns: SpendSplit[]): SpendCheck => {
  const paise = parseRupees(f.amountText);
  if (paise === null) return { paise: null, error: null, input: null, splitSum: 0 };
  if (isNaN(paise)) return { paise: null, error: 'Enter the amount in rupees, e.g. 6240 or 6240.50', input: null, splitSum: 0, field: 'amount' };
  if (paise <= 0) return { paise, error: 'Amount must be more than ₹0', input: null, splitSum: 0, field: 'amount' };
  if (paise > MAX_PAISE) return { paise, error: 'Amount is too large (max ₹10 crore)', input: null, splitSum: 0, field: 'amount' };

  const input: SpendInput = { amount: paise / 100, gst_included: f.gst };
  let splitSum = 0;
  if (campaigns.length > 1 && f.splitMode === 'manual') {
    const splits: { campaign_key: string; amount: number }[] = [];
    for (const c of campaigns) {
      const p = parseRupees(f.splitText[c.campaign_key] ?? '') ?? 0;
      if (isNaN(p) || p < 0) return { paise, error: `Check the amount for “${campaignLabel(c)}”`, input: null, splitSum, field: 'split' };
      splitSum += p;
      splits.push({ campaign_key: c.campaign_key, amount: p / 100 });
    }
    if (splitSum !== paise) {
      return {
        paise,
        error: `Campaign amounts add up to ${fmtINR(splitSum)}, but the total is ${fmtINR(paise)}`,
        input: null,
        splitSum,
        field: 'split',
      };
    }
    input.splits = splits;
  }
  return { paise, error: null, input, splitSum };
};

// ── UI ───────────────────────────────────────────────────────────────────────

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '0.64rem',
  fontWeight: 700,
  color: C.teal,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '5px',
};

const Segmented: React.FC<{
  value: string;
  options: { v: string; l: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ value, options, onChange, disabled }) => (
  <div style={{ display: 'inline-flex', gap: '1.8px', background: G.shellBg, borderRadius: '7.4px', padding: '2.8px', border: `1px solid ${G.line}` }}>
    {options.map((o) => {
      const on = value === o.v;
      return (
        <button
          key={o.v}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.v)}
          style={{
            padding: '4.6px 10px',
            borderRadius: '5.5px',
            fontSize: '0.66rem',
            fontWeight: 600,
            cursor: disabled ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
            background: on ? C.teal : 'transparent',
            color: on ? 'white' : 'rgba(17,85,80,0.6)',
            border: on ? `1px solid ${C.teal}` : '1px solid transparent',
          }}
        >
          {o.l}
        </button>
      );
    })}
  </div>
);

const MoneyInput: React.FC<{
  id?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  big?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
}> = ({ id, value, onChange, disabled, big, invalid, autoFocus }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      height: big ? '40px' : '32px',
      padding: '0 10px',
      border: `1px solid ${invalid ? 'rgba(220,38,38,0.45)' : G.line}`,
      borderRadius: '8.3px',
      background: disabled ? '#f9fafb' : C.white,
      minWidth: big ? '170px' : '110px',
      flex: big ? '1 1 170px' : '0 0 120px',
    }}
  >
    <span style={{ color: C.muted, fontWeight: 700, fontSize: big ? '0.9rem' : '0.74rem' }}>₹</span>
    <input
      id={id}
      inputMode="decimal"
      autoComplete="off"
      autoFocus={autoFocus}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder="0"
      style={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: big ? '0.95rem' : '0.74rem',
        fontWeight: 700,
        color: '#111827',
        textAlign: big ? 'left' : 'right',
      }}
    />
  </div>
);

interface SpendFieldsProps {
  value: SpendFormState;
  onChange: (v: SpendFormState) => void;
  /** Campaigns in this sync with their lead counts. */
  campaigns: SpendSplit[];
  /** Leads the amount is spread over (estimate before push). */
  leadsCount: number;
  leadsNote?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

const SpendFields: React.FC<SpendFieldsProps> = ({ value, onChange, campaigns, leadsCount, leadsNote, disabled, autoFocus }) => {
  const check = checkSpendForm(value, campaigns);
  const total = check.paise && !isNaN(check.paise) && check.paise > 0 ? check.paise : 0;
  const { incl, excl } = inclExcl(total, value.gst);
  const multi = campaigns.length > 1;
  const auto = multi ? splitByLeads(campaigns, total) : {};
  const set = (patch: Partial<SpendFormState>) => onChange({ ...value, ...patch });

  const fillByLeads = () =>
    set({
      splitMode: 'manual',
      splitText: Object.fromEntries(Object.entries(splitByLeads(campaigns, total)).map(([k, p]) => [k, rupeesText(p)])),
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
      {/* Amount + GST */}
      <div>
        <label htmlFor="spend-amount" style={LABEL}>
          Amount spent on these leads
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <MoneyInput
            id="spend-amount"
            big
            autoFocus={autoFocus}
            value={value.amountText}
            onChange={(v) => set({ amountText: v })}
            disabled={disabled}
            invalid={check.field === 'amount'}
          />
          <Segmented
            value={value.gst ? 'incl' : 'excl'}
            onChange={(v) => set({ gst: v === 'incl' })}
            disabled={disabled}
            options={[
              { v: 'incl', l: 'Includes 18% GST' },
              { v: 'excl', l: 'Before GST' },
            ]}
          />
        </div>
      </div>

      {/* Live preview */}
      {total > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '14px',
            flexWrap: 'wrap',
            padding: '8.3px 11px',
            background: G.shellBg,
            border: `1px solid ${G.line}`,
            borderRadius: '8.3px',
            fontSize: '0.68rem',
            color: C.sub,
          }}
        >
          <span>
            With GST <strong style={{ color: C.teal }}>{fmtINR(incl)}</strong>
          </span>
          <span>
            Without GST <strong style={{ color: C.teal }}>{fmtINR(excl)}</strong>
          </span>
          {leadsCount > 0 && (
            <span>
              Cost per lead <strong style={{ color: '#15803d' }}>{fmtINR(Math.round(incl / leadsCount))}</strong>
              <span style={{ color: C.muted }}> · {leadsCount} lead{leadsCount === 1 ? '' : 's'}{leadsNote ? ` ${leadsNote}` : ''}</span>
            </span>
          )}
        </div>
      )}

      {/* Campaign split */}
      {multi && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
            <span style={{ ...LABEL, marginBottom: 0 }}>Split across {campaigns.length} campaigns</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {value.splitMode === 'manual' && total > 0 && (
                <button
                  type="button"
                  onClick={fillByLeads}
                  disabled={disabled}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: 'none',
                    border: 'none',
                    padding: '0 4px',
                    color: C.tealMid,
                    fontSize: '0.64rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  <Wand2 size={11} /> Fill by leads
                </button>
              )}
              <Segmented
                value={value.splitMode}
                onChange={(v) => (v === 'manual' && !Object.keys(value.splitText).length ? fillByLeads() : set({ splitMode: v as 'auto' | 'manual' }))}
                disabled={disabled}
                options={[
                  { v: 'auto', l: 'By lead count' },
                  { v: 'manual', l: 'I’ll enter it' },
                ]}
              />
            </div>
          </div>
          <div style={{ border: `1px solid ${G.line}`, borderRadius: '8.3px', overflow: 'hidden' }}>
            {campaigns.map((c, i) => (
              <div
                key={c.campaign_key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6.5px 10px',
                  borderTop: i ? `1px solid ${G.softer}` : 'none',
                  fontSize: '0.7rem',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: c.campaign_key === 'none' ? C.muted : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {campaignLabel(c)}
                </span>
                <span style={{ color: C.muted, whiteSpace: 'nowrap', fontSize: '0.64rem' }}>
                  {c.leads_count} lead{c.leads_count === 1 ? '' : 's'}
                </span>
                {value.splitMode === 'manual' ? (
                  <MoneyInput
                    value={value.splitText[c.campaign_key] ?? ''}
                    onChange={(v) => set({ splitText: { ...value.splitText, [c.campaign_key]: v } })}
                    disabled={disabled}
                  />
                ) : (
                  <span style={{ width: '120px', textAlign: 'right', fontWeight: 700, color: total ? C.teal : C.muted }}>
                    {total ? fmtINR(auto[c.campaign_key] ?? 0) : '—'}
                  </span>
                )}
              </div>
            ))}
            {value.splitMode === 'manual' && total > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '6px',
                  padding: '6px 10px',
                  background: G.shellBg,
                  borderTop: `1px solid ${G.soft}`,
                  fontSize: '0.64rem',
                  fontWeight: 700,
                  color: check.splitSum === total ? '#15803d' : '#b45309',
                }}
              >
                {check.splitSum === total
                  ? 'Adds up to the total'
                  : check.splitSum < total
                    ? `${fmtINR(total - check.splitSum)} left to assign`
                    : `${fmtINR(check.splitSum - total)} over the total`}
              </div>
            )}
          </div>
        </div>
      )}

      {check.error && <div style={{ fontSize: '0.66rem', color: '#c62828', fontWeight: 600 }}>{check.error}</div>}
    </div>
  );
};

export default SpendFields;
